import { AlarmOnOutlined, GroupOutlined, LinkOutlined, MoreVertRounded, Tonality } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { createColumnHelper, Row } from "@tanstack/react-table";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import { CurrentTaskIcon } from "src/assets/icons";
import {
  AvatarGroup,
  BorderlessTextField,
  ItemDrawerTitle,
  Table,
  TableDrawerButton,
  TaskItemContextLabel,
  TaskItemDrawerContent,
  TaskItemFocusCharm,
  TaskItemStatusPicker,
  useDrawer,
} from "src/components";
import { DEFAULT_TASK_ITEM_STATUS, Links } from "src/constants";
import { TaskItemDto, useDeleteTaskItem, useEditTaskItem, UserDto } from "src/http";
import { useDestructiveConfirm } from "src/utilities";

const columnHelper = createColumnHelper<TaskItemDto>();

const columnsConfig = (
  handleTaskItemBlurred: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    taskItem: TaskItemDto
  ) => void,
  handleShowDrawerClicked: (e: React.MouseEvent, taskItem: TaskItemDto) => void,
  handleActionsClicked: (e: React.MouseEvent<HTMLElement>, taskItem: TaskItemDto) => void,
  theme: Theme
) => {
  return [
    columnHelper.accessor("description", {
      id: "Name",
      enableHiding: false,
      size: 400,
      header: () => (
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="caption">NAME</Typography>
        </Box>
      ),
      cell: (props) => (
        <Stack
          direction="row"
          sx={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          <BorderlessTextField
            value={props.row.original.description}
            onClick={(e) => props.row.original.userCanEdit && e.stopPropagation()}
            onBlur={(e) => handleTaskItemBlurred(e, props.row.original)}
            placeholder="Enter item..."
            isEditable={props.row.original.userCanEdit}
            maxLength={512}
          />
          <TableDrawerButton onClick={(e) => handleShowDrawerClicked(e, props.row.original)} />
        </Stack>
      ),
    }),
    columnHelper.accessor("currentStatus", {
      id: "Status",
      size: 200,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <Tonality color="disabled" />
          <Typography variant="caption">STATUS</Typography>
        </Stack>
      ),
      cell: (props) => (
        <TaskItemStatusPicker
          variant="select"
          taskItemGuid={props.row.original.guid}
          status={props.row.original.currentStatus ?? DEFAULT_TASK_ITEM_STATUS}
          disabled={!props.row.original.userCanEdit}
          ownerUserGuids={props.row.original.owners.map((x) => x.user.userId)}
          teamSlug={props.row.original.team?.slug}
        />
      ),
    }),
    columnHelper.accessor("owners", {
      id: "Ownership",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <GroupOutlined color="disabled" />
          <Typography variant="caption">OWNERSHIP</Typography>
        </Stack>
      ),
      cell: (props) => (
        <AvatarGroup
          max={5}
          users={props.row.original.owners.map((x) => x.user)}
          avatarProps={{
            size: "xsmall",
          }}
        />
      ),
    }),
    columnHelper.accessor("context", {
      id: "Context",
      size: 300,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <LinkOutlined color="disabled" />
          <Typography variant="caption">CONTEXT</Typography>
        </Stack>
      ),
      cell: (props) => {
        if (!props.row.original.context && !props.row.original.parentTaskItem) {
          return "";
        }

        return <TaskItemContextLabel taskItem={props.row.original} />;
      },
    }),
    columnHelper.accessor("dueDate", {
      id: "Due Date",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <AlarmOnOutlined color="disabled" />
          <Typography variant="caption">DUE DATE</Typography>
        </Stack>
      ),
      cell: (props) => {
        const dueDate = props.row.original.dueDate != null ? DateTime.fromISO(props.row.original.dueDate) : null;

        if (dueDate == null) {
          return null;
        } else {
          return (
            <Typography sx={{ color: dueDate < DateTime.now() ? theme.palette.error.main : undefined }}>
              {dueDate.toLocaleString(DateTime.DATE_MED)}
            </Typography>
          );
        }
      },
    }),
    columnHelper.display({
      id: "In Focus",
      size: 100,
      minSize: 100,
      meta: {
        hideColumnMenu: true,
        hideResizer: true,
      },
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <CurrentTaskIcon color="disabled" />
          <Typography variant="caption">IN FOCUS</Typography>
        </Stack>
      ),
      cell: (props) => {
        if (props.row.original.owners.length > 0) {
          return <TaskItemFocusCharm taskItem={props.row.original} />;
        }

        return null;
      },
    }),
    columnHelper.display({
      id: "Actions",
      size: 50,
      minSize: 50,
      enableResizing: false,
      enableHiding: false,
      meta: {
        hideColumnMenu: true,
        hideResizer: true,
      },
      cell: (props) => {
        return (
          <IconButton size="small" onClick={(e) => handleActionsClicked(e, props.row.original)}>
            <MoreVertRounded />
          </IconButton>
        );
      },
    }),
  ];
};

interface Props {
  tableId: string;
  taskItems: TaskItemDto[];
  enableActions: boolean;
  onExpandRowClicked: (
    row: Row<TaskItemDto>,
    isLoadingSubRows: boolean,
    setIsLoadingSubRows: (isLoading: boolean) => void
  ) => void;
  onOwnershipChanged?: (taskItemGuid: string, taskItemDescription: string, removedOwners: UserDto[]) => void;
  sx?: SxProps;
}

const TaskItemsTable = (props: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<TaskItemDto>();
  const { mutate: editTaskItem } = useEditTaskItem();
  const { mutate: deleteTaskItem } = useDeleteTaskItem();
  const { showDrawer } = useDrawer();
  const theme = useTheme();
  const confirm = useDestructiveConfirm();

  const handleTaskItemBlurred = useMemo(
    () => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, taskItem: TaskItemDto) => {
      if (taskItem.userCanEdit) {
        editTaskItem({
          taskItemGuid: taskItem.guid,
          description: e.target.value,
        });
      }
    },
    [editTaskItem]
  );

  const handleShowDrawerClicked = useMemo(
    () => (e: React.MouseEvent, taskItem: TaskItemDto) => {
      e.stopPropagation();

      showDrawer({
        title: <ItemDrawerTitle link={Links.TaskItemDetail(taskItem.guid)} />,
        content: <TaskItemDrawerContent taskItemGuid={taskItem.guid} onOwnershipChanged={props.onOwnershipChanged} />,
      });
    },
    [props.onOwnershipChanged, showDrawer]
  );

  function handleActionsClicked(e: React.MouseEvent<HTMLElement>, taskItem: TaskItemDto) {
    setMenuAnchor(e.currentTarget);
    setSelectedItem(taskItem);
    e.stopPropagation();
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    if (!selectedItem) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Task",
      content: "Are you sure you want to delete this task?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteTaskItem({
      taskItemGuid: selectedItem.guid,
    });
  }

  const columns = useMemo(
    () => columnsConfig(handleTaskItemBlurred, handleShowDrawerClicked, handleActionsClicked, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleShowDrawerClicked, handleTaskItemBlurred, theme, props.taskItems]
  );

  return (
    <>
      <Table
        tableId={props.tableId}
        data={props.taskItems}
        columns={columns}
        columnPinning={{
          right: ["In Focus", "Actions"],
        }}
        columnVisibility={{
          actions: props.enableActions,
        }}
        enableSorting={false}
        getRowCanExpand={(row) => row.original.hasSubtasks}
        getSubrows={(row) => row.subtasks ?? []}
        onExpandRowClicked={props.onExpandRowClicked}
        sx={props.sx}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export { TaskItemsTable };
